import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { _AbcGridColumn, _AbcGridComponent, DataSourceObject } from '@barthauer/abc/ui-components/abc-grid';

import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ContentReadyEvent, OptionChangedEvent, SelectionChangedEvent, Toolbar } from 'devextreme/ui/data_grid';
import { filter, Subject } from 'rxjs';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { SortMode } from './model/abc-sequencing-grid.model';

@Component({
  selector: 'lib-abc-sequencing-grid',
  imports: [_AbcGridComponent],
  templateUrl: './abc-sequencing-grid.component.html',
  styleUrl: './abc-sequencing-grid.component.css',
})
export class AbcSequencingGridComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @ViewChild('abcGrid') abcGrid!: _AbcGridComponent;
  public _columns: _AbcGridColumn[] = [];
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  protected gridToolbar: Toolbar;
  private readonly INTERNAL_ORDER_FIELD = '5247D77D-9C0C-43FA-B27D-FEBEF97133FD'; //Ensure uniqueness
  private readonly INTERNAL_ORDER_FIELD_CAPTION = 'Sequence';
  private _workingData: any[] = [];
  private selectedRowIds: string[] = [];
  private enableSortingFiltering = signal<boolean>(false);
  private sortMode = signal<SortMode>(SortMode.Manual);
  private optionChanged$ = new Subject<OptionChangedEvent>();

  @Input() keyExpr: string = 'Id';
  @Input() sortOrderColumnCaption?: string;
  @Input() set dataSourceObject(dataSourceObject: DataSourceObject) {
    if (!dataSourceObject?.dataSource || !Array.isArray(dataSourceObject?.dataSource)) {
      this._workingData = [];
      return;
    }

    const data = dataSourceObject.dataSource as any[];
    this._workingData = data.map((item, index) => {
      const clone = { ...item };
      clone[this.INTERNAL_ORDER_FIELD] = index + 1;

      return clone;
    });

    const cols = dataSourceObject?.gridColumns;
    const orderColumn = this.getOrderColumn();

    this._columns = [orderColumn, ...cols];
  }

  @Output() saveSorting = new EventEmitter<any[]>();
  @Output() cancelSorting = new EventEmitter<void>();

  constructor(
    private resourceService: ResourceManagerService,
    private messageBoxService: MessageBoxService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-sequencing-grid');
  }

  async ngOnInit(): Promise<void> {
    this.res = await this.resAsync;

    await this.initializeGridToolbarItems();
    this.abcGrid.totalCountVisibility = true;

    this.optionChanged$
      .pipe(
        filter((e) => this.sortMode() === SortMode.ByColumn),
        filter(
          (e) =>
            (e.name === 'columns' && e.fullName?.includes('filterValue')) ||
            e.name === 'filterPanel' ||
            e.name === 'filterValue',
        ),
      )
      .subscribe(() => {
        this.updateReorderButtonState();
      });
  }

  async ngAfterViewInit(): Promise<void> {
    this.applyGrid();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['dataSource'] || changes['columns']) {
      await this.initializeGridToolbarItems();
      this.applyGrid();
    }
  }

  private applyGrid(): void {
    if (!this.abcGrid) return;
    this.abcGrid.paging = { enabled: false };
    this.abcGrid.dataSourceObject = { dataSource: this._workingData, gridColumns: this._columns };

    this.abcGrid.rowDragging = {
      allowReordering: true,
      showDragIcons: true,
      onReorder: (e: any) => this.handleReorder(e),
    };
    this.abcGrid.instance?.option('sorting.mode', 'none');
  }

  private refreshGrid(): void {
    const field = this.getOrderField();

    this._workingData.forEach((item, index) => {
      item[field] = index + 1;
    });
    this.abcGrid.dataSource = [...this._workingData];
  }

  private getOrderField(): string {
    return this.INTERNAL_ORDER_FIELD;
  }

  private getOrderColumn(): _AbcGridColumn {
    const orderColumn: _AbcGridColumn = {
      dataField: this.getOrderField(),
      caption: this.sortOrderColumnCaption ?? this.INTERNAL_ORDER_FIELD_CAPTION,
      width: 120,
      allowEditing: false,
    };

    return orderColumn;
  }

  private handleReorder(e: any): void {
    const field = this.getOrderField();
    const grid = e.component;
    const visibleRows = grid.getVisibleRows();
    const fromIndex = this._workingData.indexOf(e.itemData);
    const toIndex = this._workingData.indexOf(visibleRows[e.toIndex].data);

    if (fromIndex === -1 || toIndex === -1) {
      return;
    }

    this._workingData.splice(fromIndex, 1);
    this._workingData.splice(toIndex, 0, e.itemData);

    this._workingData.forEach((item, index) => {
      item[field] = index + 1;
    });
    this.abcGrid.dataSource = [...this._workingData];
  }

  public ngOnDestroy(): void {}

  private async initializeGridToolbarItems(): Promise<void> {
    this.gridToolbar = {
      visible: true,
      items: [
        {
          location: 'before',
          name: 'groupPanel',
        },
        {
          location: 'before',
          widget: 'dxButton',
          cssClass: 'move-up-btn',
          name: 'MoveUp',
          options: {
            hint: this.res.get('MoveUp'),
            icon: await this.res?.svgContent('MoveUp-Image'),
            onClick: () => this.moveUp(),
          },
        },
        {
          location: 'before',
          widget: 'dxButton',
          cssClass: 'move-down-btn',
          options: {
            hint: this.res.get('MoveDown'),
            icon: await this.res?.svgContent('MoveDown-Image'),
            onClick: () => this.moveDown(),
          },
        },
        {
          location: 'before',
          widget: 'dxButton',
          options: {
            hint: this.res.get('Reorder'),
            icon: await this.res?.svgContent('Reorder-Image'),
            disabled: true,
            onClick: () => {
              this.confirmReorder(this.res.get('ReorderConfirmationText'));
            },
          },
        },
        {
          location: 'before',
          widget: 'dxButton',
          options: {
            hint: this.res.get('Save'),
            icon: await this.res?.svgContent('Save-Image'),
            onClick: () => this.onSave(),
          },
        },
        {
          location: 'before',
          widget: 'dxButton',
          options: {
            hint: this.res.get('Cancel'),
            icon: await this.res?.svgContent('Cancel-Image'),
            onClick: () => this.onCancel(),
          },
        },
        {
          location: 'after',
          template: () => {
            const div = document.createElement('div');
            div.innerText = this.res.get('SortMode');
            div.classList.add('sort-mode-text');
            return div;
          },
        },
        {
          location: 'after',
          widget: 'dxButtonGroup',
          cssClass: 'sort-mode-switch',
          options: {
            items: [
              { id: 'Manual', text: this.res.get('Manual') },
              { id: 'ByColumn', text: this.res.get('ByColumn') },
            ],
            keyExpr: 'id',
            selectionMode: 'single',
            stylingMode: 'text',
            onInitialized: (e) => {
              e.component.option('selectedItemKeys', [this.sortMode()]);
            },
            onSelectionChanged: (e) => {
              this.sortMode.set(e.addedItems[0]?.id);
              this.toggleColumnSorting();
            },
          },
        },
        { name: 'searchPanel', location: 'after' },
      ],
    };
  }

  private toggleColumnSorting(): void {
    const grid = this.abcGrid.instance;
    const enabled = this.sortMode() === SortMode.ByColumn;
    this.enableSortingFiltering.set(enabled);

    if (!!grid) {
      grid.beginUpdate();

      if (!enabled) {
        grid.clearSorting();
        grid.clearFilter();

        grid.option('sorting.mode', 'none');
        grid.option('rowDragging.allowReordering', true);
      } else {
        grid.option({
          sorting: { mode: 'single' },
          rowDragging: { allowReordering: false },
        });
      }

      this.updateButtonStates();
      this.abcGrid.headerFilter.visible = enabled;
      this.abcGrid.filterRow.visible = enabled;

      grid.endUpdate();
    }
  }

  private updateButtonStates(): void {
    const grid = this.abcGrid.instance;
    if (!!grid) {
      this.updateMoveButtonsState();
      grid.option('toolbar.items[3].options.disabled', !this.enableSortingFiltering());
    }
  }

  protected onCancel(): void {
    this.cancelSorting.emit();
  }

  protected onSave(): void {
    const field = this.getOrderField();
    const sorted = [...this._workingData].sort((a, b) => a[field] - b[field]).map((item) => ({ ...item }));

    const result = sorted.map((item) => {
      const clone = { ...item };
      delete clone[field];
      return clone;
    });

    this.saveSorting.emit(result);
  }

  protected moveDown(): void {
    if (!this.selectedRowIds.length) {
      return;
    }

    const data = [...this._workingData];

    const indexes = this.selectedRowIds
      .map((id) => data.findIndex((item) => item[this.keyExpr] === id))
      .filter((i) => i !== -1 && i < data.length - 1)
      .sort((a, b) => b - a);

    if (!indexes.length) {
      return;
    }

    for (const index of indexes) {
      if (!this.selectedRowIds.includes(data[index + 1][this.keyExpr])) {
        [data[index], data[index + 1]] = [data[index + 1], data[index]];
      }
    }

    this._workingData = data;

    const anchorIndex = indexes[0];

    this.abcGrid.instance?.selectRows(this.selectedRowIds, true);
    this.abcGrid.instance?.navigateToRow(data[anchorIndex][this.keyExpr]);

    this.refreshGrid();
    this.updateMoveButtonsState();
  }

  protected moveUp(): void {
    if (!this.selectedRowIds.length) {
      return;
    }

    const data = [...this._workingData];

    const indexes = this.selectedRowIds
      .map((id) => data.findIndex((item) => item[this.keyExpr] === id))
      .filter((i) => i > 0)
      .sort((a, b) => a - b);

    if (!indexes.length) {
      return;
    }

    for (const index of indexes) {
      if (index > 0 && !this.selectedRowIds.includes(data[index - 1][this.keyExpr])) {
        [data[index - 1], data[index]] = [data[index], data[index - 1]];
      }
    }

    this._workingData = data;

    const anchorIndex = indexes[0];

    this.abcGrid.instance?.selectRows(this.selectedRowIds, true);
    this.abcGrid.instance?.navigateToRow(data[anchorIndex][this.keyExpr]);

    this.refreshGrid();
    this.updateMoveButtonsState();
  }

  protected onReorder(): void {
    const field = this.getOrderField();
    this.abcGrid.instance
      .getDataSource()
      .items()
      .forEach((dataItem: any, index: number) => {
        dataItem[field] = index + 1;
      });

    this._workingData = this.abcGrid.instance.getDataSource().items();
    this.abcGrid.dataSource = [...this._workingData];
  }

  protected onSelectionChanged(e: SelectionChangedEvent): void {
    this.selectedRowIds = e.selectedRowKeys ?? [];
    this.updateMoveButtonsState();
  }

  public onGridOptionChanged(e: OptionChangedEvent): void {
    this.optionChanged$.next(e);
  }

  private updateReorderButtonState(): void {
    const grid = this.abcGrid.instance;

    Promise.resolve().then(() => {
      const combinedFilter = grid.getCombinedFilter();
      const hasFilter = !!combinedFilter;

      grid.option('toolbar.items[3].options.disabled', hasFilter);
    });
  }

  public onGridContentReady(e: ContentReadyEvent): void {
    if (this.selectedRowIds.length || !this._workingData.length) {
      return;
    }

    const firstKey = this._workingData[0][this.keyExpr];
    e.component.selectRows([firstKey], false);
  }

  protected async confirmReorder(message: string): Promise<void> {
    const messageSettings = new MessageSettings();
    messageSettings.mode = MessageMode.Question;
    messageSettings.icon = MessageBoxIcon.Question;
    messageSettings.button = MessageBoxButtons.YesNo;
    messageSettings.message = message;

    const result = await this.messageBoxService.showMessageBox(messageSettings).toPromise();
    if (result['result'] === DialogResult.Yes) {
      this.onReorder();
    }
  }

  private updateMoveButtonsState(): void {
    const grid = this.abcGrid.instance;
    if (!grid || !this.selectedRowIds.length) {
      return;
    }

    const data = this._workingData;

    const indexes = this.selectedRowIds
      .map((id) => data.findIndex((item) => item[this.keyExpr] === id))
      .filter((i) => i !== -1)
      .sort((a, b) => a - b);

    const firstIndex = indexes[0];
    const lastIndex = indexes[indexes.length - 1];

    const disableMoveUp = firstIndex === 0;
    const disableMoveDown = lastIndex === data.length - 1;

    grid.option('toolbar.items[1].options.disabled', disableMoveUp || this.enableSortingFiltering());
    grid.option('toolbar.items[2].options.disabled', disableMoveDown || this.enableSortingFiltering());
  }
}
